import { createInstance, type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * 프로젝트 기본 namespace.
 *
 * 현재 구조에서는 feature 패키지의 모든 resource를
 * translation namespace에 flat하게 합치는 방식을 사용한다.
 */
const DEFAULT_NS = 'translation';

/**
 * scbk-services에서만 i18n instance를 생성한다.
 *
 * 중요:
 * feature package, core package에서는 절대 createInstance()를 호출하지 않는다.
 * npm install 환경에서 package가 중복 설치되더라도
 * app에서는 scbk-services가 제공하는 이 instance만 사용해야 한다.
 */
const i18n = createInstance();

/**
 * init 완료 여부.
 *
 * i18next의 isInitialized도 사용하지만,
 * 내부적으로 명시적인 상태를 함께 관리해서 중복 init을 방지한다.
 */
let initialized = false;

/**
 * 동시에 initI18n이 여러 번 호출되는 상황을 막기 위한 Promise Lock.
 *
 * 예:
 * - React StrictMode
 * - 여러 bootstrap이 동시에 실행되는 경우
 * - micro frontend 형태로 여러 module이 동시에 로딩되는 경우
 */
let initializing: Promise<I18nInstance> | null = null;

export interface InitI18nOptions {
    /**
     * 현재 사용할 언어.
     *
     * 예: ko, en, ko-KR
     */
    lng: string;

    /**
     * fallback 언어.
     *
     * 기본값: ko
     */
    fallbackLng?: string;

    /**
     * 기본 namespace.
     *
     * 기본값: translation
     */
    defaultNS?: string;

    /**
     * 사용할 namespace 목록.
     *
     * 기본값: ['translation']
     */
    ns?: string[];

    /**
     * i18next debug 옵션.
     *
     * 운영에서는 false 권장.
     */
    debug?: boolean;
}

/**
 * Singleton i18n instance 반환.
 *
 * Provider, resource 등록, language 변경 모두 이 instance를 사용한다.
 */
export const getI18n = (): I18nInstance => {
    return i18n;
};

/**
 * i18n 초기화 여부 반환.
 */
export const isI18nInitialized = (): boolean => {
    return initialized || i18n.isInitialized;
};

/**
 * i18n 최초 초기화 함수.
 *
 * 책임:
 * - initReactI18next 연결
 * - i18next init
 * - 중복 init 방지
 * - 이미 초기화된 경우 language 변경만 수행
 *
 * 주의:
 * - React Component render 내부에서 호출하지 않는다.
 * - main.tsx 또는 bootstrap 단계에서 먼저 호출한다.
 */
export const initI18n = async ({
    lng,
    fallbackLng = 'ko',
    defaultNS = DEFAULT_NS,
    ns = [DEFAULT_NS],
    debug = false,
}: InitI18nOptions): Promise<I18nInstance> => {
    /**
     * 이미 초기화된 경우 재초기화하지 않는다.
     * 언어만 달라졌다면 changeLanguage만 수행한다.
     */
    if (isI18nInitialized()) {
        if (lng && i18n.language !== lng) {
            await i18n.changeLanguage(lng);
        }

        return i18n;
    }

    /**
     * 초기화 진행 중이면 기존 Promise를 그대로 반환한다.
     * 이것으로 중복 init을 방지한다.
     */
    if (initializing) {
        return initializing;
    }

    initializing = i18n
        .use(initReactI18next)
        .init({
            lng,
            fallbackLng,
            defaultNS,
            ns,
            debug,
            interpolation: {
                /**
                 * React는 기본적으로 XSS escape를 수행한다.
                 * react-i18next 공식 권장 설정이다.
                 */
                escapeValue: false,
            },
            react: {
                /**
                 * Suspense를 사용하지 않는 구조.
                 * 기존 앱 라우팅/Provider 구조에서 예측 가능하게 동작한다.
                 */
                useSuspense: false,
            },
        })
        .then(() => {
            initialized = true;
            initializing = null;
            return i18n;
        })
        .catch((error) => {
            initializing = null;
            throw error;
        });

    return initializing;
};

/**
 * 언어 변경 함수.
 *
 * 앱 실행 중 locale 변경이 필요한 경우 사용한다.
 * feature package에서는 호출하지 않는다.
 */
export const changeI18nLanguage = async (lng: string): Promise<I18nInstance> => {
    if (lng && i18n.language !== lng) {
        await i18n.changeLanguage(lng);
    }

    return i18n;
};

export default i18n;
